import get from 'lodash/get';
import { all, put, takeEvery, select, call } from 'redux-saga/effects';
import { reset, destroy, stopSubmit, clearSubmitErrors } from 'redux-form';
import {
  CHANGE_PASSWORD_FORM,
  CREATE_AUTHORISED_PERSON_FORM,
  CREATE_AUTHORISED_PERSON_MODAL,
  CREATE_BANK_DETAILS_FORM,
  CREATE_BANK_DETAILS_MODAL,
  DELETE_AUTHORISED_PERSON_MODAL,
  DELETE_BANK_DETAILS_MODAL,
  EMAIL_NOTIFICATIONS_SETTINGS_FORM,
  EMAIL_NOTIFICATION_MODAL,
  PROFILE_CHANGE_PASSWORD_MODAL,
  UNSUBSCRIBE_MODAL_SUCCESS,
  EDIT_OTHER_DOCUMENTS_MODAL,
  EDIT_OTHER_DOCUMENTS_FORM,
} from '../constants';

import * as actionCreators from '../actions';
import * as modalActionCreators from 'modules/common/CustomModal/actions';
import * as notificationsActions from 'modules/notifications/actions';
import {
  getVerificationUserRequest,
  initializePersonalDetails,
  getVerificationUserSuccess,
} from '../../ProfileVerification/actions';
import * as geolocationActions from 'modules/geolocation/actions';
import { getUserId } from 'modules/auth/selectors';
import { updateUserPhoneNumber, refreshToken } from 'modules/auth/actions';
import { getUpdatedUserRequest } from '../../../../menu/actions';
import { ACCOUNT_STATUSES } from 'constants/index';
import { getNotificationStyle } from '../selectors';
import {
  createAuthorisedPersonFail,
  createAuthorisedPersonSuccess,
  createAuthorisedPersonRequest,
  deleteAuthorisedPersonFail,
  deleteAuthorisedPersonSuccess,
  getAuthorisedPersonsRequest,
  saveEmailNotificationsSettingsFail,
  saveEmailNotificationsSettingsSuccess,
  getMailchimpStatusRequest,
  saveEmailNotificationsSettingsRequest,
  searchBankDetailsFail,
  subscribeEmailNotificationsSettingsRequestSuccess,
  unsubscribeEmailNotificationsSettingsRequestSuccess,
  unsubscribeEmailNotificationsSettingsRequestFail,
  subscribeEmailNotificationsSettingsRequestFail,
  createBankDetailsRequest,
  createBankDetailsSuccess,
  createBankDetailsFail,
  otherDocumentsUpdateSuccess,
  otherDocumentsUpdateFail,
} from '../actions';
import { getAccountsRequest } from 'modules/accounts/pages/MyAccounts/actions';
import { closeModal, openModal } from 'components/TP-UI/TPModal/actions';
import { getLpoaRequest, revokeLpoaFail, revokeLpoaSuccess } from 'modules/lpoa/actions';
import showErrorMessage from 'utils/showErrorMessage';

function* changePasswordSuccess(action) {
  yield put(refreshToken(action.response.data.token));

  yield all([
    put(
      notificationsActions.showNotificationInfo({
        key: 'profile:profile_details.notifications.passwordChanged',
      }),
    ),
    put(reset(CHANGE_PASSWORD_FORM)),
    put(closeModal(PROFILE_CHANGE_PASSWORD_MODAL)),
  ]);
}

function* changePasswordFail(action) {
  const { error } = action;

  const message = { key: 'profile:profile_details.notifications.passwordFailed' };
  const key = get(error, 'data.message.key');
  switch (key) {
    case 'backend:samePassword':
      message.key = 'profile:profile_details.notifications.samePassword';
      break;
    case 'backend:oldPassIncorrect':
      message.key = 'profile:profile_details.notifications.oldPasswordIncorrect';
      break;
    default:
      break;
  }

  yield put(stopSubmit(CHANGE_PASSWORD_FORM, { _error: message }));
}

function* changePasswordRequest() {
  yield put(clearSubmitErrors(CHANGE_PASSWORD_FORM));
}

function* pageOpened() {
  const userId = yield select(getUserId);
  yield put(getVerificationUserRequest(userId));
  yield put(geolocationActions.getCountriesAction());
  yield put(getAuthorisedPersonsRequest());
  yield put(getAccountsRequest({ isLive: true }));
  yield put(getMailchimpStatusRequest({ userId }));
}

function* initialieProfileDetailsForm({ response }) {
  const user = response.data;
  yield put(updateUserPhoneNumber(get(user, 'profile.phoneNumber')));

  if (user.verification.status !== ACCOUNT_STATUSES.APPROVED) {
    yield put(initializePersonalDetails());
  }
}

function* handleCreateBankDetailsFail() {
  yield put(
    stopSubmit(CREATE_BANK_DETAILS_FORM, {
      _error: 'profile:profile_bank_details.notifications.accountFailed',
    }),
  );
}

function* handleCreateBankDetailsSuccess() {
  yield put(closeModal(CREATE_BANK_DETAILS_MODAL));
  yield put(destroy(CREATE_BANK_DETAILS_FORM));

  yield put(
    notificationsActions.showNotificationInfo({
      key: `profile:profile_bank_details.notifications.accountCreated`,
    }),
  );
}

function* closeDeleteBankDetailModal(action) {
  const notification = action.error ? 'accountRemoveFailed' : 'accountRemoved';
  const notificationStyle = getNotificationStyle(action);
  yield all([
    put(modalActionCreators.closeModal(DELETE_BANK_DETAILS_MODAL)),
    put(
      notificationsActions[notificationStyle]({
        key: `profile:profile_bank_details.notifications.${notification}`,
      }),
    ),
  ]);
}

function* handleCreateAuthorisedPersonSuccess() {
  yield all([
    put(
      notificationsActions.showNotificationInfo({
        key: 'profile:authorisedPersons.notifications.created',
      }),
    ),
    put(getAuthorisedPersonsRequest()),
    put(closeModal(CREATE_AUTHORISED_PERSON_MODAL)),
    put(reset(CREATE_AUTHORISED_PERSON_FORM)),
  ]);
}

function* handleCreateAuthorisedPersonFail() {
  yield put(
    stopSubmit(CREATE_AUTHORISED_PERSON_FORM, {
      _error: 'profile:authorisedPersons.notifications.createFailed',
    }),
  );
}

function* handleDeleteAuthorisedPersonRequest() {
  yield put(clearSubmitErrors(CREATE_AUTHORISED_PERSON_FORM));
}

function* handleDeleteAuthorisedPersonSuccess() {
  yield all([
    put(
      notificationsActions.showNotificationInfo({
        key: 'profile:authorisedPersons.notifications.removed',
      }),
    ),
    put(getAuthorisedPersonsRequest()),
    put(closeModal(DELETE_AUTHORISED_PERSON_MODAL)),
  ]);
}

function* handleDeleteAuthorisedPersonFail() {
  yield all([
    put(
      notificationsActions.showNotificationError({
        key: 'profile:authorisedPersons.notifications.removeFailed',
      }),
    ),
  ]);
}

function* handleCreateBankDetailsRequest() {
  yield put(clearSubmitErrors(CREATE_BANK_DETAILS_FORM));
}

function* handleSaveEmailNotificationsSettingsSuccess() {
  const userId = yield select(getUserId);

  yield all([put(getMailchimpStatusRequest({ userId })), put(getVerificationUserRequest(userId))]);

  yield put(closeModal(EMAIL_NOTIFICATION_MODAL));

  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:emailNotificationsSettings.notification.success',
    }),
  );
}

function* handleSaveEmailNotificationsSettingsFail() {
  yield put(
    stopSubmit(EMAIL_NOTIFICATIONS_SETTINGS_FORM, {
      _error: 'profile:emailNotificationsSettings.notification.fail',
    }),
  );
}

function* handleSubscribeEmailNotificationsSettingsRequestSuccess() {
  const userId = yield select(getUserId);
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:emailNotificationsSettings.notification.success',
    }),
  );
  yield put(getVerificationUserRequest(userId));
  yield put(getMailchimpStatusRequest({ userId }));
}

function* handleUnsubscribeEmailNotificationsSettingsRequestSuccess() {
  yield put(openModal(UNSUBSCRIBE_MODAL_SUCCESS));
  const userId = yield select(getUserId);
  yield put(getVerificationUserRequest(userId));
  yield put(getMailchimpStatusRequest({ userId }));
}
function* handleUnsubscribeEmailNotificationsSettingsRequestFail() {
  yield put(
    notificationsActions.showNotificationError({
      key: 'backend:somethingWrongContactHelp',
    }),
  );
}
function* handleSubscribeEmailNotificationsSettingsRequestFail() {
  yield put(
    notificationsActions.showNotificationError({
      key: 'backend:somethingWrongContactHelp',
    }),
  );
}

function* handleSaveEmailNotificationsSettingsRequest() {
  yield put(clearSubmitErrors(EMAIL_NOTIFICATIONS_SETTINGS_FORM));
}

function* handleRevokeLpoaFail(action) {
  yield call(showErrorMessage, action);
}

function* handleRevokeLpoaSuccess() {
  yield put(notificationsActions.showNotificationInfo('Limited Power of Attorney was revoked'));
  yield put(getLpoaRequest());
}

function* handleSearchBankDetailsFail(action) {
  yield call(showErrorMessage, action);
}

function* handleOtherDocumentsUpdateSuccess() {
  const userId = yield select(getUserId);
  yield put(getUpdatedUserRequest(userId));

  yield put(closeModal(EDIT_OTHER_DOCUMENTS_MODAL));
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:otherDocuments.notifications.updated',
    }),
  );
}

function* handleOtherDocumentsUpdateFail(action) {
  const { message: errorMessage } = get(action, 'error.data', {
    key: 'profile:otherDocuments.notifications.updateFailed',
  });

  yield put(
    stopSubmit(EDIT_OTHER_DOCUMENTS_FORM, {
      _error: errorMessage,
    }),
  );
}

export default function* watchRequest() {
  yield all([
    takeEvery(actionCreators.changePasswordSuccess, changePasswordSuccess),
    takeEvery(actionCreators.changePasswordFail, changePasswordFail),
    takeEvery(actionCreators.changePasswordRequest, changePasswordRequest),
    takeEvery(actionCreators.profilePageOpened, pageOpened),
    takeEvery(getVerificationUserSuccess, initialieProfileDetailsForm),
    takeEvery(createBankDetailsRequest, handleCreateBankDetailsRequest),
    takeEvery(createBankDetailsFail, handleCreateBankDetailsFail),
    takeEvery(createBankDetailsSuccess, handleCreateBankDetailsSuccess),
    takeEvery(actionCreators.deleteBankDetailsFail, closeDeleteBankDetailModal),
    takeEvery(actionCreators.deleteBankDetailsSuccess, closeDeleteBankDetailModal),
    takeEvery(createAuthorisedPersonRequest, handleDeleteAuthorisedPersonRequest),
    takeEvery(createAuthorisedPersonSuccess, handleCreateAuthorisedPersonSuccess),
    takeEvery(createAuthorisedPersonFail, handleCreateAuthorisedPersonFail),
    takeEvery(deleteAuthorisedPersonSuccess, handleDeleteAuthorisedPersonSuccess),
    takeEvery(deleteAuthorisedPersonFail, handleDeleteAuthorisedPersonFail),
    takeEvery(saveEmailNotificationsSettingsSuccess, handleSaveEmailNotificationsSettingsSuccess),
    takeEvery(saveEmailNotificationsSettingsFail, handleSaveEmailNotificationsSettingsFail),
    takeEvery(saveEmailNotificationsSettingsRequest, handleSaveEmailNotificationsSettingsRequest),
    takeEvery(revokeLpoaSuccess, handleRevokeLpoaSuccess),
    takeEvery(revokeLpoaFail, handleRevokeLpoaFail),
    takeEvery(searchBankDetailsFail, handleSearchBankDetailsFail),
    takeEvery(
      unsubscribeEmailNotificationsSettingsRequestSuccess,
      handleUnsubscribeEmailNotificationsSettingsRequestSuccess,
    ),
    takeEvery(
      unsubscribeEmailNotificationsSettingsRequestFail,
      handleUnsubscribeEmailNotificationsSettingsRequestFail,
    ),
    takeEvery(
      subscribeEmailNotificationsSettingsRequestFail,
      handleSubscribeEmailNotificationsSettingsRequestFail,
    ),
    takeEvery(
      subscribeEmailNotificationsSettingsRequestSuccess,
      handleSubscribeEmailNotificationsSettingsRequestSuccess,
    ),

    takeEvery(otherDocumentsUpdateSuccess, handleOtherDocumentsUpdateSuccess),
    takeEvery(otherDocumentsUpdateFail, handleOtherDocumentsUpdateFail),
  ]);
}
